import { useTheme, alpha } from '@mui/material/styles';
import { Box, Grid, Typography, Container, Stack, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { CheckCircleFilled } from '@ant-design/icons';
import { ExpandingDigitalRing } from 'sections/ExpandingDigitalRing';
import DocumentRequestForm from 'sections/landing/DocumentRequestForm';

// ==============================|| LANDING TOP (ファーストビュー) ||============================== //
//
// 左：訴求メッセージ / 右：資料請求フォーム の2カラム構成。
// 右側をフォームにしているのは、ファーストビューの段階でリード獲得を成立させるため。
// CTAボタンを別途置くのではなくフォーム自体を置くことで、離脱前に接点を取りにいく。

const LandingTop = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const primaryColor = '#2aa1af';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.6 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: 'spring', stiffness: 100, damping: 20 }
    }
  };

  // 決裁者(経営者)に最初に見せるのは収益に直結する加算の話。
  // 項目は後段の BillingSupportSection で詳述している加算と揃えている。
  const additionDocuments = [
    '専門的支援加算（体制・実施）',
    '不登校支援加算（個別サポート加算Ⅲ）',
    '家族支援加算・関係機関との連携書類　など'
  ];

  // OHANA AI が扱う書類の範囲を一目で示すキーワード
  const keywords = ['個別支援計画', '加算書類', '減算アラート', 'モニタリング', '音声入力', 'AIアセスメント'];

  return (
    <Box
      component="header"
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        // 淡い単色の背景。以前は右からティール色の面がスライドインしていたが、
        // フォームを置いた今は動きが読み取りの邪魔になるため、静的な背景にしている。
        background: `linear-gradient(180deg, #ffffff 0%, #f5fbfb 45%, ${alpha(primaryColor, 0.07)} 100%)`,
        py: { xs: 10, md: 12 }
      }}
    >
      {/* --- 背景装飾：ブランドカラーの曲線。クローラーには無視させる --- */}
      <Box
        component="svg"
        aria-hidden="true"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      >
        <path
          d="M -120 700 C 220 700 250 250 520 250 C 760 250 780 560 1020 560 C 1260 560 1300 240 1560 240"
          fill="none"
          stroke={alpha(primaryColor, 0.16)}
          strokeWidth="46"
          strokeLinecap="round"
        />
        <path
          d="M -120 810 C 260 810 300 380 600 380 C 860 380 880 660 1120 660 C 1340 660 1380 380 1560 380"
          fill="none"
          stroke={alpha(primaryColor, 0.08)}
          strokeWidth="26"
          strokeLinecap="round"
        />
      </Box>

      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={isMobile ? 6 : 5} alignItems="center">
          {/* ============ 左側：訴求メッセージ ============ */}
          <Grid item xs={12} md={6.5}>
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              {/* タイトルとデジタルリング */}
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: { xs: '150px', md: '215px' },
                    width: '100%',
                    mb: 3
                  }}
                >
                  <ExpandingDigitalRing isMobile={isMobile} primaryColor={primaryColor} delay={1.0} />

                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      color: '#1a1a1a',
                      fontSize: { xs: '2.4rem', sm: '3.2rem', md: '3.7rem' },
                      fontWeight: 800,
                      lineHeight: 1.15,
                      letterSpacing: '-0.03em'
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: { xs: '0.9rem', md: '1.05rem' },
                        color: primaryColor,
                        mb: 2,
                        fontWeight: 700,
                        letterSpacing: '0.05em'
                      }}
                    >
                      児童発達支援・放課後等デイサービス向け
                    </Box>
                    支援計画書を、
                    <br />
                    <Box component="strong" sx={{ color: 'inherit', fontWeight: 'inherit' }}>
                      AIで自動作成
                    </Box>
                    。
                  </Typography>
                </Box>
              </motion.div>

              {/* 加算取得の訴求 */}
              <motion.div variants={itemVariants}>
                <Box
                  component="section"
                  sx={{
                    mb: 3.5,
                    maxWidth: '620px'
                  }}
                >
                  <Typography
                    variant="h2"
                    component="h2"
                    sx={{
                      color: '#1a1a1a',
                      fontSize: { xs: '1.15rem', md: '1.35rem' },
                      fontWeight: 800,
                      lineHeight: 1.6,
                      mb: 2.2
                    }}
                  >
                    {/* マーカー風の強調。擬似要素＋z-index:-1 だと背景に隠れることがあるため、
                        背景グラデーションで下線を描いている（折り返しにも追従する） */}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline',
                        backgroundImage: `linear-gradient(transparent 62%, ${alpha(primaryColor, 0.28)} 62%)`
                      }}
                    >
                      算定に必要な書類
                    </Box>
                    の作成をサポート！
                  </Typography>

                  <Stack spacing={1.3} component="ul" sx={{ p: 0, listStyle: 'none', m: 0 }}>
                    {additionDocuments.map((item) => (
                      <Stack key={item} direction="row" spacing={1.4} component="li" alignItems="flex-start">
                        <CheckCircleFilled
                          aria-hidden="true"
                          style={{ color: primaryColor, fontSize: '1.05rem', marginTop: '4px', flexShrink: 0 }}
                        />
                        <Typography
                          component="span"
                          sx={{ color: '#374151', fontSize: { xs: '0.92rem', md: '1rem' }, lineHeight: 1.7, fontWeight: 600 }}
                        >
                          {item}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </motion.div>

              {/* 価格。ここは一目で分かる signal に留め、料金体系の説明は PricingSection が担当する */}
              <motion.div variants={itemVariants}>
                <Box sx={{ maxWidth: '620px' }}>
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ columnGap: 1, rowGap: 0.5 }}
                  >
                    <Typography component="span" sx={{ fontWeight: 800, color: '#374151', fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                      月額
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: '3.4rem', md: '4.4rem' },
                        color: primaryColor,
                        letterSpacing: '-0.04em',
                        lineHeight: 1
                      }}
                    >
                      7,700
                    </Typography>
                    <Typography component="span" sx={{ fontWeight: 800, color: '#374151', fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                      円
                    </Typography>
                    <Typography component="span" sx={{ fontWeight: 700, color: '#8a9393', fontSize: { xs: '0.82rem', md: '0.9rem' } }}>
                      （税込）/ 教室
                    </Typography>
                  </Stack>

                  {/* 契約のハードルの低さは決裁の後押しになるため、価格のすぐ下に添える */}
                  <Typography
                    component="p"
                    sx={{ color: primaryColor, fontWeight: 800, fontSize: { xs: '0.82rem', md: '0.9rem' }, mt: 1.2 }}
                  >
                    1ヶ月の無料トライアルあり ・ 初期費用なし ・ 解約はいつでも可能
                  </Typography>
                </Box>
              </motion.div>

              {/* 区切り線 */}
              <Box
                aria-hidden="true"
                sx={{ height: '1px', bgcolor: alpha(primaryColor, 0.2), maxWidth: '620px', my: 3 }}
              />

              {/* 扱う書類のキーワード */}
              <motion.div variants={itemVariants}>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ maxWidth: '620px' }}>
                  {keywords.map((keyword) => (
                    <Box
                      key={keyword}
                      sx={{
                        px: 1.8,
                        py: 0.8,
                        borderRadius: '999px',
                        bgcolor: '#fff',
                        border: `1px solid ${alpha(primaryColor, 0.25)}`,
                        color: primaryColor,
                        fontSize: { xs: '0.76rem', md: '0.82rem' },
                        fontWeight: 800
                      }}
                    >
                      {keyword}
                    </Box>
                  ))}
                </Stack>
              </motion.div>
            </motion.div>
          </Grid>

          {/* ============ 右側：資料請求フォーム ============
            *
            * スマホでは1カラムに畳まれ、訴求メッセージの直下に積まれる。
            * 以前は入力欄がファーストビューを占めるのを避けて非表示にしていたが、
            * スマホ流入のリード獲得機会を落としていたため表示に戻している。
            */}
          <Grid item xs={12} md={5.5}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
            >
              <DocumentRequestForm />
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingTop;
